import React, {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useRef,
  useState,
} from "react";
import useServerSentEvents from "../hooks/useServerSentEvents";
import useEditConflictDetection from "../hooks/useEditConflictDetection";

const SSEContext = createContext();

export const useSSE = () => {
  const context = useContext(SSEContext);
  if (!context) {
    throw new Error("useSSE must be used within SSEProvider");
  }
  return context;
};

export const SSEProvider = ({ children }) => {
  // Subscribers live in a ref (not state) since routing a message just calls
  // each callback directly - looping over them inside a setState updater
  // caused "Cannot update a component while rendering a different component"
  // whenever a subscriber's callback called its own setState.
  const subscribersRef = useRef(new Map());
  const [lastEventTime, setLastEventTime] = useState(null);

  // Initialize global conflict detection
  const conflictDetector = useEditConflictDetection();

  // Global SSE message handler
  const handleSSEMessage = useCallback((message, conflict) => {
    //console.log("Global SSE message received:", message);
    setLastEventTime(new Date());

    // Route message to relevant subscribers
    subscribersRef.current.forEach((callback, subscriberId) => {
      try {
        //console.log(`Routing message to subscriber: ${subscriberId}`);
        // Call each subscriber with the message and conflict
        callback(message, conflict);
      } catch (error) {
        console.error(`Error in SSE subscriber ${subscriberId}:`, error);
      }
    });
  }, []); // No dependencies - function is stable

  // Single global SSE connection
  const { isConnected, error } = useServerSentEvents(
    `${process.env.REACT_APP_API_BASE_URL || "http://localhost:4004"}/api/v1/sse/updates`,
    handleSSEMessage,
    {
      conflictDetector,
      withCredentials: true,
      maxReconnectAttempts: 10,
      reconnectInterval: 2000,
    },
  );

  // Subscribe to SSE updates
  const subscribe = useCallback((subscriberId, callback) => {
    //console.log(`SSE subscriber added: ${subscriberId}`);
    subscribersRef.current.set(subscriberId, callback);

    // Return unsubscribe function
    return () => {
      //console.log(`SSE subscriber removed: ${subscriberId}`);
      subscribersRef.current.delete(subscriberId);
    };
  }, []);

  // Send update notifications (for optimistic updates)
  const notifyUpdate = useCallback(
    (updateData) => {
      // Broadcast to all subscribers
      handleSSEMessage(updateData, null);
    },
    [handleSSEMessage],
  );

  const value = {
    // Connection state
    isConnected,
    error,
    lastEventTime,

    // Subscription management
    subscribe,
    notifyUpdate,

    // Global conflict detection
    conflictDetector,

    // Stats
    subscriberCount: subscribersRef.current.size,
  };

  return <SSEContext.Provider value={value}>{children}</SSEContext.Provider>;
};
